import type { PlanPurchaseCoordinator } from './plan-purchase.js';

type Vendor = 'iris' | 'ptzoptics';
interface PaymentReturn {
  vendor: Vendor;
  actorKey?: string;
  roomId: string;
  sessionId: string;
}
interface Attachment {
  store: PlanPurchaseCoordinator;
  currentActor: () => string | undefined;
}
const accountKey = (vendor: Vendor, actorKey: string) => `${vendor}:${actorKey}`;
const stores = new Map<string, Attachment>();
// Readers carry identity only, so a detached route cannot retain credentials.
const readers = new Map<Vendor, () => string | undefined>();
const pending: PaymentReturn[] = [];
const panelListeners = new Map<string, Set<() => void>>();
const pendingPanels = new Set<string>();
const coldPanels = new Set<Vendor>();

function claimColdReturns(vendor: Vendor, actorKey: string): void {
  for (const value of pending) {
    if (value.vendor === vendor && value.actorKey === undefined) value.actorKey = actorKey;
  }
  if (coldPanels.delete(vendor)) pendingPanels.add(accountKey(vendor, actorKey));
}

// The Electron IPC path only carries identity. Keeping transport out of this
// channel avoids the Electron -> broker -> limits -> Electron import cycle.
export function recordPlanPaymentReturn(vendor: Vendor, roomId: string, sessionId = ''): void {
  if (
    !/^[a-f0-9]{32}$/i.test(roomId) ||
    (sessionId !== '' && !/^cs_[a-zA-Z0-9_]+$/.test(sessionId))
  )
    return;
  const actorKey = readers.get(vendor)?.();
  const key = actorKey === undefined ? undefined : accountKey(vendor, actorKey);
  const attachment = key === undefined ? undefined : stores.get(key);
  if (attachment === undefined || attachment.currentActor() !== actorKey) {
    if (
      !pending.some(
        (value) =>
          value.vendor === vendor &&
          value.actorKey === actorKey &&
          value.roomId === roomId &&
          value.sessionId === sessionId
      )
    )
      pending.push({ vendor, ...(actorKey === undefined ? {} : { actorKey }), roomId, sessionId });
  } else {
    const { store } = attachment;
    store.returned(roomId, sessionId);
    void store.resume(roomId);
  }
  const listeners = key === undefined ? undefined : panelListeners.get(key);
  if (key === undefined) coldPanels.add(vendor);
  else if (listeners === undefined || listeners.size === 0) pendingPanels.add(key);
  else for (const listener of listeners) listener();
}

// A return can arrive before the Studio mounts. Replay only the request to
// display billing; it conveys no paid entitlement and never changes Studio.
export function subscribePlanPaymentReturns(
  vendor: Vendor,
  actorKey: string,
  listener: () => void
): () => void {
  const key = accountKey(vendor, actorKey);
  let listeners = panelListeners.get(key);
  if (listeners === undefined) {
    listeners = new Set();
    panelListeners.set(key, listeners);
  }
  const notify = () => {
    if (readers.get(vendor)?.() === actorKey) listener();
  };
  listeners.add(notify);
  if (readers.get(vendor)?.() === actorKey && pendingPanels.delete(key)) notify();
  return () => {
    listeners.delete(notify);
    if (listeners.size === 0 && panelListeners.get(key) === listeners) panelListeners.delete(key);
  };
}

export function attachPlanPurchaseReturns(
  vendor: Vendor,
  store: PlanPurchaseCoordinator,
  currentActor: () => string | undefined
): () => void {
  const key = accountKey(vendor, store.actorKey);
  const attachment = { store, currentActor };
  stores.set(key, attachment);
  readers.set(vendor, currentActor);
  if (currentActor() === store.actorKey) claimColdReturns(vendor, store.actorKey);
  for (let index = pending.length - 1; index >= 0; index -= 1) {
    const value = pending[index];
    if (
      value?.vendor === vendor &&
      value.actorKey === store.actorKey &&
      currentActor() === store.actorKey
    ) {
      store.returned(value.roomId, value.sessionId);
      pending.splice(index, 1);
    }
  }
  // An earlier route's cleanup must not detach its replacement.
  return () => {
    if (stores.get(key) === attachment) stores.delete(key);
  };
}
